import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCheckoutStoreType,
  getStoreTimeSlots,
  selectCheckoutTimeSlot,
  selectPickupType,
  updateExternalDeliveryId,
  getCartList
} from "redux/actions";
import "./delivery-info.scss";
import { useLocation, useParams } from "react-router-dom";
import { getLocalStorageValue, isKeyurSite } from "config/helper";
import useShippingLabel from 'hooks/useShippingLabel';
import { CUSTOMER_TIMEZONE, DEBUG, PLACE_DURING_BUSINESS_HOUR, PROCESSING_DATE_AND_TIME } from 'config/constant';
import useOrderDetails from 'hooks/useOrderDetails';
import useStoreTimeSlots from 'hooks/useStoreTimeSlots';
import { useGetCustomerAddressList } from 'hooks/useGetCustomerAddressList';
import { useStoreDetails } from 'hooks/useStoreDetails';
import { useConfig } from 'hooks/useConfig';
import { useApiDefaults } from 'hooks/useApiDefaults';

const DeliveryInfo = ({
  handleSelectPaymentMethod,
}) => {
  const selectDateAndTimeOptions = useStoreTimeSlots()
  const dispatch = useDispatch();
  const { checkoutType } = useParams();
  const userToken = getLocalStorageValue("user-token");
  const memberNumber = getLocalStorageValue("member-number");
  const clientId = getLocalStorageValue("RSAclient-id");
  const isUserLoggedIn = useSelector(({ user }) => user.isUserLoggedIn);
  const { currentStoreID, storeDetails } = useSelector(({ store }) => store);
  const storeId = currentStoreID || getLocalStorageValue("selected-store-id");
  const { doorDashLable, isCateringMeal, isMealkit, isCatering, isPickup, isDelivery } = useConfig();

  const [pickupType, setPickupType] = useState("later");

  const [showDatePopup, setShowDatePopup] = useState(false);

  const { shippingLabelLower, shippingLabelTitle } = useShippingLabel()

  const shippingMethod = getShippingMethod(storeDetails, shippingLabelLower)

  const modifyOrderId = useSelector(({ checkout }) => checkout.modifyOrderId);
  useOrderDetails()

  const {
    checkoutStoreType,
    isFollowedEbtPayment,
    checkoutTimeSlot,
    checkoutTimeSlotName,
    storeTimeSlotsLoading,
    savePlaceOrder,
    storeTimeSlotsError
  } = useSelector(({ checkout }) => checkout);
  useGetCustomerAddressList();

  const {
    shipping_day = "",
    shipping_date = "",
    shipping_time = "",
  } = (isFollowedEbtPayment ? savePlaceOrder : checkoutTimeSlot) || {}

  const {
    allow_pickup = "0",
    allow_delivery = "0",
    allow_mealkit_pickup = "0",
    allow_mealkit_delivery = "0",
    allow_catering_pickup = "0",
    allow_catering_delivery = "0",
    allow_mealkit_asap = "0",
  } = storeDetails || {};

  const { cateringAndMealKitProductCartList } = useSelector(
    ({ cartWishlist }) => cartWishlist
  );

  const { is_mealkit = "0" } = cateringAndMealKitProductCartList[0] || {};

  useEffect(() => {
    if (modifyOrderId || storeTimeSlotsLoading) return;

    const value = selectDateAndTimeOptions[0]?.value ?? {}
    const name = selectDateAndTimeOptions[0]?.title ?? PLACE_DURING_BUSINESS_HOUR
    dispatch(selectCheckoutTimeSlot({value, title: name}));
  }, [dispatch, selectDateAndTimeOptions?.length, checkoutStoreType, modifyOrderId]); // eslint-disable-line

  const pickupTypeRef = useRef(null)

  useEffect(() => {
    const ebtSplitPaymentContinue = getLocalStorageValue('ebt_split_payment_continue') === "true"
    if (ebtSplitPaymentContinue) {
      if (DEBUG) debugger
      return
    }

    if (!storeDetails || modifyOrderId) {
       if (DEBUG) debugger
      return
    }

    const refValue = `${is_mealkit ?? ""}-${checkoutType ?? ""}-${modifyOrderId ?? ""}`
    if (refValue && refValue === pickupTypeRef.current) return
    pickupTypeRef.current = refValue

    let pickupType = allow_pickup // grocery
    if (isCateringMeal) {
      if (isMealkit) {
        // mealkit
        pickupType = allow_mealkit_pickup
      } else if (isCatering) {
        // catering
        pickupType = allow_catering_pickup
      }
    }

    const type = pickupType === "1" ? "store_pickup" : "delivery"
    dispatch(selectCheckoutStoreType(type))
  }, [storeDetails, is_mealkit, checkoutType, modifyOrderId, pickupTypeRef]) // eslint-disable-line

  useStoreDetails();

  useEffect(() => {
    const body = {
      RSAClientId: clientId,
      ClientStoreId: +storeId || 1,
      AppName: isUserLoggedIn ? "shop" : "",
      member_number: isUserLoggedIn ? memberNumber : "",
      user_token: isUserLoggedIn ? userToken : "",
      Version: isUserLoggedIn ? "1" : "",
      DeviceType: isUserLoggedIn ? "web" : "",
      timezone: CUSTOMER_TIMEZONE,
    };
    const url =
      checkoutType === "catering-meal"
        ? "/GetCateringAndMealkitPickupSlots"
        : "/AogGetStoreTimeSlots";
    dispatch(getStoreTimeSlots(body, url));
  }, [dispatch, storeId, checkoutType]); // eslint-disable-line

  const apiDefaults = useApiDefaults();
  const location = useLocation()
  useEffect(() => {
    const onCheckoutPage = location?.pathname?.startsWith("/checkout");
    if (!checkoutTimeSlot || !isUserLoggedIn || onCheckoutPage) return;

    dispatch(getCartList(apiDefaults));
  }, [checkoutTimeSlot, dispatch, isUserLoggedIn, location?.pathname]);

  const handleChange = (e) => {
    const { value } = e.target;
    dispatch(selectCheckoutStoreType(value));
    dispatch(updateExternalDeliveryId(""));
    handleSelectPaymentMethod(value);
  };
  const handlePickupTypeChange = (e) => {
    const { value } = e.target;
    setPickupType(value);
    dispatch(selectPickupType(value));
    dispatch(updateExternalDeliveryId(""));
  };

  const handleChangeDropDownOption = (value, name) => {
    dispatch(selectCheckoutTimeSlot({value, title: name}));
    dispatch(updateExternalDeliveryId(""));
  };

  const getShippingDateAndTimeLabel = () => {
    if (checkoutStoreType === "delivery" && (clientId === "334" || isKeyurSite())) {
      return PROCESSING_DATE_AND_TIME
    }

    if (checkoutStoreType === "store_pickup") {
      return "Choose pickup date and time" 
    }

    return `Choose ${shippingLabelLower} date and time`
  }

  function PickupTitle() {
    let prefix = "Choose "
    if (storeTimeSlotsError) {
      prefix = ""
    }

    let label = ""
    if (checkoutStoreType === "store_pickup") {
      label = `${prefix}pickup date and time`
    } else {
      label = `${prefix}${shippingLabelLower} date and time`
    }

    return <p>{label[0].toUpperCase() + label.slice(1)}</p>
  }

  useEffect(() => {
    if (showDatePopup){
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return()=>{
      document.body.style.overflow = "auto";
    };
  }, [showDatePopup]);

  
  return (
    <div className="delivery-info-wrapper">
      {isFollowedEbtPayment && (
        <div className="ebt-checkout-information">
          <span>
            <strong>Shipping Method :</strong>{" "}
            {savePlaceOrder?.shipping_method === "store_pickup" ? "Store Pickup" : shippingLabelTitle}
          </span>
          <span>
            <strong>Shipping Day :</strong> {shipping_day}
          </span>
          <span>
            <strong>Shipping Date :</strong> {shipping_date}
          </span>
          <span>
            <strong>Shipping Time :</strong> {shipping_time}
          </span>
        </div>
      )}

      {!isFollowedEbtPayment && (
        <div className="delivery-info-section">
          {/* // Grocery */}
          {!checkoutType && (
            <div className="grid">
              <div className="grid-items">
                <p>Choose store {shippingMethod}</p>
                {storeDetails && (
                  <div className="radio-button-alignment">
                    {allow_pickup === "1" ? (
                      <div>
                        <input
                          type="radio"
                          value="store_pickup"
                          id="pickup"
                          name="deliveryType"
                          onChange={handleChange}
                          checked={checkoutStoreType === "store_pickup"}
                        />
                        <label htmlFor="pickup">Pickup</label>
                      </div>
                    ) : (
                      ""
                    )}
                    {allow_delivery === "1" ? (
                      <div>
                        <input
                          type="radio"
                          value="delivery"
                          id="delivery"
                          name="deliveryType"
                          onChange={handleChange}
                          checked={checkoutStoreType === "delivery"}
                        />
                        <label htmlFor="delivery">{shippingLabelTitle}{doorDashLable}</label>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                )}
              </div>
              <div className="grid-items">
                <p> {getShippingDateAndTimeLabel(checkoutStoreType, shippingLabelLower, clientId)} </p>
                <ShippingDateTimePicker
                    options={selectDateAndTimeOptions}
                    handleChangeDropDownOption={handleChangeDropDownOption}
                    checkoutTimeSlotName={checkoutTimeSlotName}
                    showDatePopup={showDatePopup}
                    setShowDatePopup={setShowDatePopup}
                  />
              </div>
            </div>
          )}
          {/* // Catering and mealkit */}
          {isCateringMeal && (
            <div className="grid">
              <div className="grid-items">
                <p>Choose store pickup or {shippingLabelLower}</p>
                {storeDetails && (
                  <div className="radio-button-alignment">
                    {allow_mealkit_pickup === "1" && isMealkit ? (
                      <div>
                        <input
                          type="radio"
                          value="store_pickup"
                          id="pickup"
                          name="deliveryType"
                          onChange={handleChange}
                          checked={checkoutStoreType === "store_pickup"}
                        />
                        <label htmlFor="pickup">Pickup</label>
                      </div>
                    ) : (
                      ""
                    )}
                    {allow_mealkit_delivery === "1" && isMealkit ? (
                      <div>
                        <input
                          type="radio"
                          value="delivery"
                          id="delivery"
                          name="deliveryType"
                          onChange={handleChange}
                          checked={isDelivery}
                        />
                        <label htmlFor="delivery">{shippingLabelTitle}{doorDashLable}</label>
                      </div>
                    ) : (
                      ""
                    )}
                    {allow_catering_pickup === "1" && isCatering ? (
                      <div>
                        <input
                          type="radio"
                          value="store_pickup"
                          id="pickup"
                          name="deliveryType"
                          onChange={handleChange}
                          checked={isPickup}
                        />
                        <label htmlFor="pickup">Pickup</label>
                      </div>
                    ) : (
                      ""
                    )}
                    {allow_catering_delivery === "1" && isCatering ? (
                      <div>
                        <input
                          type="radio"
                          value="delivery"
                          id="delivery"
                          name="deliveryType"
                          onChange={handleChange}
                          checked={isDelivery}
                        />
                        <label htmlFor="delivery">{shippingLabelTitle}{doorDashLable}</label>
                      </div>
                    ) : (
                      ""
                    )}
                  </div>
                )}
                {isMealkit && isPickup && (
                  <div className="radio-button-alignment">
                    {allow_mealkit_asap === "1" &&
                      <div>
                        <input
                          type="radio"
                          value="asap"
                          id="asap"
                          name="pickupType"
                          onChange={handlePickupTypeChange}
                          checked={pickupType === "asap"}
                        />
                        <label htmlFor="asap">ASAP</label>
                      </div>
                    }
                    <div>
                      <input
                        type="radio"
                        value="later"
                        id="later"
                        name="pickupType"
                        onChange={handlePickupTypeChange}
                        checked={pickupType === "later"}
                      />
                      <label htmlFor="later">Schedule for later</label>
                    </div>
                  </div>
                )}
              </div>
              {isMealkit &&
                (isDelivery ||
                  pickupType === "later") && (
                  <div className="grid-items">
                    <PickupTitle />
                    <ShippingDateTimePicker
                      options={selectDateAndTimeOptions}
                      handleChangeDropDownOption={handleChangeDropDownOption}
                      checkoutTimeSlotName={checkoutTimeSlotName}
                      storeTimeSlotsError={storeTimeSlotsError}
                      showDatePopup={showDatePopup}
                      setShowDatePopup={setShowDatePopup}
                      />
                  </div>
                )}
              {isCatering && (
                <div className="grid-items">
                  <PickupTitle />
                  <ShippingDateTimePicker
                    options={selectDateAndTimeOptions}
                    handleChangeDropDownOption={handleChangeDropDownOption}
                    checkoutTimeSlotName={checkoutTimeSlotName}
                    storeTimeSlotsError={storeTimeSlotsError}
                    showDatePopup={showDatePopup}
                    setShowDatePopup={setShowDatePopup}
                    />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function getShippingMethod(storeDetails, shippingLabelLower) {
  if (!storeDetails) return ""

  if (storeDetails?.allow_pickup === "1" && storeDetails?.allow_delivery === "1") {
    return `pickup or ${shippingLabelLower}`
  }

  if (storeDetails?.allow_pickup === "1") {
    return "pickup"
  }

  if (storeDetails?.allow_delivery === "1") {
    return shippingLabelLower
  }

  return ""
}

function ShippingDateTimePicker({
  options,
  handleChangeDropDownOption,
  checkoutTimeSlotName,
  storeTimeSlotsError,
  showDatePopup,
  setShowDatePopup
}) {
  
  if (storeTimeSlotsError) {
    return <p className="error-text">{storeTimeSlotsError}</p>    
  }

  if (showDatePopup) {
    return (
      <DateTimePickerPopup 
        options={options} 
        onClose={() => setShowDatePopup(false)}
        onSave={(data) => {
          handleChangeDropDownOption(data.value, data.title)
          setShowDatePopup(false)
        }} 
      />
    )
  }

  return (
    <div className="custom-date-picker">
      <div className="selected-time-display" onClick={() => setShowDatePopup(true)}>
        <i className="fa-solid fa-clock"></i>
        <span>{checkoutTimeSlotName || "Select Time Slot"}</span>
        <i className="fa-solid fa-chevron-down arrow-icon"></i>
      </div>
    </div>
  )
}

function DateTimePickerPopup({ options = [], onClose, onSave }) {
  const { checkoutTimeSlot, storeTimeSlotsError } = useSelector(({ checkout }) => checkout);

  const [dateList, timeMap] = useMemo(() => {
    const dates = new Set()
    const times = new Map()

    for (const item of options) {
      const date = item.value?.shipping_date
      if (!date) continue
      
      dates.add(date)
      
      if (!times.has(date)) {
        times.set(date, [])
      }
      times.get(date).push(item)
    }

    return [Array.from(dates), times]
  }, [options])

  const [selectedDate, setSelectedDate] = useState(checkoutTimeSlot?.shipping_date)
  const availableSlots = timeMap.get(selectedDate) || []
  const [selectedSlot, setSelectedSlot] = useState(availableSlots?.find(i => i?.value?.shipping_index === checkoutTimeSlot?.shipping_index))

  const handleDateSelect = (date) => {
    setSelectedDate(date)

    const timeSlots = timeMap.get(date) || []
    const timeAvailableInDate = timeSlots.find(i => i?.value?.shipping_time === selectedSlot?.value?.shipping_time)
    if (timeAvailableInDate) {
      setSelectedSlot(timeAvailableInDate)
    } else {
      setSelectedSlot(timeSlots[0])
    }
  }

  const handleTimeSelect = (slot) => {
    setSelectedSlot(slot)
  }

  const handleSave = () => {
    if (selectedSlot && onSave) {
      onSave(selectedSlot)
    } else if (onClose) {
        onClose()
    }
  }

  if (storeTimeSlotsError) {
    return <p className="error-text">{storeTimeSlotsError}</p> 
  }

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <div className="popup-header">
            <h3>Select Time Slot</h3>
            <button onClick={onClose} className="close-btn">
             <i className="fa-solid fa-xmark"></i>
            </button>
        </div>

        <div className="date-time-wrap">
          <div className="date-section">
            <span className="section-title">Date</span>
            <ul className="date-list">
              {dateList.map(date => {
                const dayName = getDayText(date)
                const dateObj = parseLocalDate(date);
                const dateText = dateObj.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                })
                const isActive = selectedDate === date
                return (
                  <li
                    key={date}
                    onClick={() => handleDateSelect(date)}
                    className={isActive ? "active" : ""}
                  >
                    <span>{dayName}</span>
                    <span>{dateText}</span>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="time-section">
            <span className="section-title">Time</span>
            <ul className="time-list">
              {availableSlots.map((slotItem) => {
                const isActive = selectedSlot?.value?.shipping_index === slotItem.value?.shipping_index
                const timeLabel = slotItem.value?.shipping_time || ""
                const parts = timeLabel.includes("-") ? timeLabel.split("-").map(p => p.trim()) : [timeLabel]

                return (
                  <li
                    key={slotItem.value?.shipping_index}
                    onClick={() => handleTimeSelect(slotItem)}
                    className={isActive ? "active" : ""}
                  >
                     {parts.length > 1 ? (
                        <>
                            <span>{parts[0]}</span>
                            <span>{parts[1]}</span>
                        </>
                    ) : (
                        <span>{timeLabel}</span>
                    )}
                  </li>
                )
              })}
            </ul>
            {availableSlots.length === 0 && <p>No time slots available for this date.</p>}
          </div>
          <div className="popup-footer">
            <div className="save-btn">
              <button onClick={handleSave}>Confirm Slot</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function parseLocalDate(dateString) {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function getDayText(dateString) {
  const check = parseLocalDate(dateString);
  check.setHours(0, 0, 0, 0);
  return check.toLocaleDateString('en-US', { weekday: 'long' });
}

export default DeliveryInfo;
